import React, { useState } from 'react';
import { History, Search, Trash2, Calendar, ExternalLink, RefreshCw } from 'lucide-react';
import Button from '../../../../../components/ui/Button';

/**
 * Компонент для отслеживания истории проверок URL
 */
const HistoryTracker = ({ history, onClearHistory, onCheckUrl }) => {
  const [searchTerm, setSearchTerm] = useState('');
  
  if (!history || history.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2 flex items-center">
          <History className="w-5 h-5 mr-2 text-indigo-500" />
          История проверок
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Здесь будет отображаться история ваших проверок URL.
        </p>
      </div>
    );
  }

  // Фильтрация истории по поисковому запросу
  const filteredHistory = searchTerm
    ? history.filter(item => 
        item.url.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : history;

  // Форматирование даты
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  // Получение иконки для отображения статуса URL
  const getStatusIcon = (summary) => {
    if (summary.hasErrors) {
      return <RefreshCw className="w-4 h-4 text-red-500" />;
    } else if (summary.redirectCount > 0) {
      return <RefreshCw className="w-4 h-4 text-blue-500" />;
    } else {
      return <ExternalLink className="w-4 h-4 text-green-500" />;
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <History className="w-5 h-5 mr-2 text-indigo-500" />
        История проверок ({history.length})
      </h3>

      {/* Поиск */}
      <div className="mb-4 relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <Search className="h-4 w-4 text-gray-400" />
        </div>
        <input
          type="text"
          className="form-input pl-10"
          placeholder="Поиск по URL"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      {/* Список истории */}
      <div className="space-y-2 max-h-80 overflow-y-auto custom-scrollbar pr-1">
        {filteredHistory.length > 0 ? (
          filteredHistory.map((item) => (
            <div 
              key={item.id} 
              className="p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/30 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              <div className="flex items-start justify-between">
                <div className="flex-grow">
                  <div className="flex items-center">
                    {getStatusIcon(item.summary)}
                    <span className="ml-2 text-sm font-medium text-gray-900 dark:text-white truncate max-w-[200px]">
                      {item.url}
                    </span>
                  </div>
                  <div className="flex items-center mt-1 text-xs text-gray-500 dark:text-gray-400">
                    <Calendar className="w-3 h-3 mr-1" />
                    {formatDate(item.date)}
                  </div>
                </div>
                <button
                  onClick={() => onCheckUrl(item.url)}
                  className="ml-2 p-1 text-gray-500 hover:text-indigo-600 dark:text-gray-400 dark:hover:text-indigo-400"
                  title="Проверить снова"
                >
                  <RefreshCw className="w-4 h-4" />
                </button>
              </div>
              <div className="mt-1.5 text-xs text-gray-500 dark:text-gray-400">
                {item.summary.redirectCount > 0 ? (
                  <span>
                    Редиректов: {item.summary.redirectCount} • 
                    Время: {item.summary.totalTime}мс •
                    {item.summary.isSecureConnection ? ' HTTPS' : ' HTTP'}
                  </span>
                ) : (
                  <span>
                    Прямая загрузка • 
                    Время: {item.summary.totalTime}мс •
                    {item.summary.isSecureConnection ? ' HTTPS' : ' HTTP'}
                  </span>
                )}
              </div>
            </div>
          ))
        ) : (
          <div className="text-center py-4 text-gray-500 dark:text-gray-400">
            История не найдена. Попробуйте изменить поисковый запрос.
          </div>
        )}
      </div>

      {/* Кнопка очистки истории */}
      {history.length > 0 && (
        <div className="mt-4 flex justify-end">
          <Button
            type="button"
            variant="outline"
            icon={<Trash2 className="w-4 h-4" />}
            onClick={onClearHistory}
            size="sm"
          >
            Очистить историю
          </Button>
        </div>
      )}
    </div>
  );
};

export default HistoryTracker;